import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MobileLayoutProps } from './MobileLayout.types';
import { adaptMobileLayoutProps } from './MobileLayout.adapter';
import './MobileLayout.css';
import { useTranslation } from '../../../redux/TranslationHelper';

/**
 * MobileLayout 컴포넌트
 */
const MobileLayoutComponent: React.FC<any> = (props) => {
  // 어댑터를 통해 props 변환
  const adaptedProps = useMemo(() => adaptMobileLayoutProps(props), [props]);
  const {
    style = {},
    className = '',
    componentProps = {},
    menuItems: propMenuItems,
    showArrows: propShowArrows,
    id = '',
    content,
    data,
    ...htmlProps
  } = adaptedProps;

  const navigate = useNavigate();
  
  // 다국어 처리를 위한 훅 사용
  const { t } = useTranslation();
  
  // 모바일 감지 (window.innerWidth 기준)
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768
  );

  // 현재 활성화된 메뉴 경로
  const [activePath, setActivePath] = useState<string>("");

  // 컴포넌트 속성에서 값 가져오기
  const menuItems = useMemo(() => propMenuItems || componentProps.menuItems || [
    { label: t("내 정보"), path: "/mydata" },
    { label: t("자산정보"), path: "/points" },
    { label: t("추천인"), path: "/recommendee" },
    { label: t("계보도"), path: "/legs" },
    { label: t("멤버십투자"), path: "/getmembership" },
    { label: t("수당요약"), path: "/getPaybacks" },
  ], [propMenuItems, componentProps.menuItems, t]);
  
  const showArrows = propShowArrows !== undefined 
    ? propShowArrows 
    : componentProps.showArrows !== undefined 
      ? componentProps.showArrows 
      : true;
  
  const backgroundColor = componentProps.backgroundColor || "#fff";
  const textColor = componentProps.textColor || "#000";
  const activeTextColor = componentProps.activeTextColor || "#3b82f6";
  const scrollAmount = componentProps.scrollAmount || 100;

  const menuRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // 화면 크기 변경 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // 초기 설정
    handleResize();
    
    // 이벤트 리스너 등록
    window.addEventListener("resize", handleResize);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // 현재 경로 감지
  useEffect(() => {
    if (typeof window !== "undefined") {
      const currentPath = window.location.pathname;
      setActivePath(currentPath);
    }
  }, []);

  // 화살표 표시 여부 확인
  const checkArrows = useCallback(() => {
    const el = menuRef.current;
    if (!el) return;
    
    setShowLeftArrow(el.scrollLeft > 0);
    setShowRightArrow(el.scrollWidth > el.clientWidth + el.scrollLeft);
  }, []);

  // 메뉴 스크롤 이벤트 및 화살표 표시 여부 확인
  useEffect(() => {
    checkArrows();
    
    const el = menuRef.current;
    if (el) {
      el.addEventListener("scroll", checkArrows);
      window.addEventListener("resize", checkArrows);
    }
    
    return () => {
      if (el) {
        el.removeEventListener("scroll", checkArrows);
        window.removeEventListener("resize", checkArrows);
      }
    };
  }, [checkArrows]);

  // 메뉴 스크롤 함수
  const scrollMenu = useCallback((dir: "left" | "right") => {
    const el = menuRef.current;
    if (!el) return;
    
    el.scrollBy({
      left: dir === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth"
    });
  }, [scrollAmount]);

  // 메뉴 클릭 핸들러
  const handleMenuClick = useCallback((path: string) => {
    navigate(path);
    setActivePath(path);
  }, [navigate]);

  // 모바일 아닐 경우 렌더링하지 않음
  if (!isMobile) return null;

  // 컨테이너 스타일
  const containerStyle = {
    background: backgroundColor,
    borderBottom: "1px solid #ccc",
    ...style
  };

  // 메뉴 아이템 스타일
  const menuItemStyle = {
    color: textColor
  };

  // 활성 메뉴 아이템 스타일
  const activeMenuItemStyle = {
    color: activeTextColor,
    fontWeight: 600
  };

  return (
    <div 
      className={`mobile-layout-container ${className}`}
      style={containerStyle}
      id={id}
      {...htmlProps}
    >
      {showArrows && showLeftArrow && (
        <div
          className="mobile-layout-arrow-left"
          onClick={() => scrollMenu("left")}
        >
          ◀
        </div>
      )}
      
      <div
        ref={menuRef}
        className="mobile-layout-menu"
      >
        <div className="mobile-layout-items">
          {menuItems.map((item) => (
            <div
              key={item.path}
              onClick={() => handleMenuClick(item.path)}
              className="mobile-layout-item"
              style={activePath === item.path ? activeMenuItemStyle : menuItemStyle}
            >
              {t(item.label)}
            </div>
          ))}
        </div>
      </div>
      
      {showArrows && showRightArrow && (
        <div
          className="mobile-layout-arrow-right"
          onClick={() => scrollMenu("right")}
        >
          ▶
        </div>
      )}
    </div>
  );
};

export default MobileLayoutComponent; 